<template>
  <div v-if="isLoading" class="my-10">
    <BaseFlexSpinner />
  </div>

  <div v-else-if="analysis" class="flex flex-col gap-5">
    <!-- Program identity band + reward rule -->
    <LoyaltyCardProgramBand
      :name="programName"
      :organization-name="organizationName"
      :rule-label="ruleLabel"
      :exportable="!!analysis"
      :exporting="exporting"
      @export="exportAll"
    />

    <!-- Migration hook banner: big buyers still on a paper card -->
    <div
      v-if="convertibleCount"
      class="bg-white border-[1.5px] border-lightPurple rounded-lg px-6 py-4 flex items-center justify-between gap-6 flex-wrap"
    >
      <div class="flex items-center gap-4">
        <div class="w-12 h-12 rounded-xl bg-primary-lightest flex items-center justify-center shrink-0">
          <Icon name="mdi-swap-horizontal-bold" :size="26" color="#3513e2" />
        </div>
        <div>
          <div class="font-semibold text-base">
            Encore des cartes papier {{ organizationName ?? programName }} ?
          </div>
          <div class="text-sm text-slate-500">
            Apodis a repéré
            <b class="text-gray-dark">{{ formatInt(convertibleCount) }} gros acheteur{{ convertibleCount > 1 ? 's' : '' }}</b>
            de la gamme suivie sans carte digitale.
          </div>
        </div>
      </div>
      <button
        type="button"
        class="bg-primary text-white rounded-lg px-4 py-2 text-sm font-semibold transition-opacity hover:opacity-90"
        :disabled="!canOpenConsumers"
        @click="goToMigration"
      >
        Lancer la migration
      </button>
    </div>

    <!-- KPI row -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      <LoyaltyCardKpiCard
        label="Chiffre d'affaires"
        icon="mdi-cash-multiple"
        accent="brand"
        :value="formatEuro(revenue ?? null)"
        :pending="revenue === undefined"
        :subtitle="revenue === null ? 'aucun achat sur la gamme suivie' : 'achats de la gamme suivie'"
      />
      <LoyaltyCardKpiCard
        label="Cartes ouvertes"
        icon="mdi-card-multiple-outline"
        :value="formatInt(membershipCount)"
        :clickable="canOpenConsumers"
        subtitle="voir les cartes clients"
        @click="goToConsumers"
      />
      <LoyaltyCardKpiCard
        label="Panier moyen"
        icon="mdi-basket-outline"
        :value="formatEuro(averageCartPrice)"
        :trend="cartTrend"
        subtitle="par carte du programme"
      />
      <LoyaltyCardKpiCard
        label="Récompenses délivrées"
        icon="mdi-gift-outline"
        :value="formatInt(rewardsSummary?.count ?? null)"
        :pending="rewardsSummary === undefined"
        :subtitle="rewardsSummary ? `${formatEuro(rewardsSummary.value)} offerts` : null"
        :clickable="canOpenConsumers && !!rewardsSummary"
        @click="goToRewards"
      />
      <LoyaltyCardKpiCard
        label="Proches du seuil"
        icon="mdi-bell-ring-outline"
        accent="warning"
        :value="formatInt(nearThreshold ?? null)"
        :pending="nearThreshold === undefined"
        :subtitle="nearThresholdSubtitle"
        :clickable="canOpenConsumers && nearThreshold !== undefined"
        @click="goToNearThreshold"
      />
      <LoyaltyCardKpiCard
        label="Fréquence d'achat moyenne"
        icon="mdi-calendar-sync-outline"
        :value="typeof frequency === 'number' ? `${frequency} j` : null"
        :pending="frequency === undefined"
        :subtitle="frequency === null ? 'au moins 2 passages par carte requis' : 'entre deux achats'"
      />
    </div>

    <!-- Charts row 1 -->
    <div class="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4">
      <LoyaltyCardChartCard
        title="Répartition des cartes par progression"
        :subtitle="progressionSubtitle"
        :pending="!hasProgression"
        pending-hint="Nécessite l'enrichissement de l'analyse (répartition par palier)."
      >
        <div class="flex flex-col gap-3.5">
          <div
            v-for="(bucket, index) in analysis.progression"
            :key="bucket.range"
            class="flex items-center gap-3"
          >
            <div class="w-28 text-[13px] flex items-center gap-2 shrink-0">
              <span
                class="w-2.5 h-2.5 rounded-sm"
                :style="{ background: progressionColors[index % progressionColors.length] }"
              />
              {{ bucket.range }}
            </div>
            <div class="flex-1 h-5 bg-gray-light rounded-full overflow-hidden">
              <div
                class="h-full rounded-full"
                :style="{
                  width: `${(bucket.count / progressionMax) * 100}%`,
                  background: progressionColors[index % progressionColors.length]
                }"
              />
            </div>
          </div>
        </div>
      </LoyaltyCardChartCard>

      <LoyaltyCardChartCard
        title="Répartition des porteurs"
        :subtitle="`${formatInt(membershipCount)} porteurs`"
        :pending="!membershipCount"
        pending-hint="Aucun porteur pour le moment."
      >
        <LoyaltyCardGenderChart
          :male="genderCount.male"
          :female="genderCount.female"
          :total="membershipCount ?? 0"
        />
      </LoyaltyCardChartCard>
    </div>

    <!-- Charts row 2 -->
    <div class="grid grid-cols-1 lg:grid-cols-[1.4fr_1fr] gap-4">
      <LoyaltyCardChartCard
        title="Pyramide des âges des porteurs"
        :subtitle="averageAge !== null ? `Âge moyen ${averageAge} ans` : null"
        :pending="!hasAgePyramid"
        pending-hint="Nécessite l'enrichissement de l'analyse (répartition par tranche d'âge)."
      >
        <template #legend>
          <div class="flex items-center gap-3 text-[11px] text-slate-500">
            <span class="flex items-center gap-1.5">
              <span class="w-2.5 h-2.5 rounded-sm" :style="{ background: LOYALTY_CARD_GENDER_COLORS.female }" />Femmes
            </span>
            <span class="flex items-center gap-1.5">
              <span class="w-2.5 h-2.5 rounded-sm" :style="{ background: LOYALTY_CARD_GENDER_COLORS.male }" />Hommes
            </span>
          </div>
        </template>
        <div class="flex flex-col gap-2.5">
          <div
            v-for="bucket in analysis.age_pyramid"
            :key="bucket.range"
            class="flex items-center gap-2.5 text-xs"
          >
            <div class="flex-1 flex justify-end">
              <div
                class="h-4 rounded-l"
                :style="{ width: `${(bucket.female / pyramidMax) * 100}%`, background: LOYALTY_CARD_GENDER_COLORS.female }"
              />
            </div>
            <div class="w-12 text-center text-slate-500 tabular-nums">
              {{ bucket.range }}
            </div>
            <div class="flex-1">
              <div
                class="h-4 rounded-r"
                :style="{ width: `${(bucket.male / pyramidMax) * 100}%`, background: LOYALTY_CARD_GENDER_COLORS.male }"
              />
            </div>
          </div>
        </div>
      </LoyaltyCardChartCard>

      <LoyaltyCardChartCard
        title="Cartes ouvertes par mois"
        :pending="!hasMonthlyOpenings"
        pending-hint="Nécessite l'enrichissement de l'analyse (série mensuelle des ouvertures)."
      >
        <div class="flex items-end justify-around gap-2.5 h-[150px]">
          <div
            v-for="month in analysis.monthly_openings"
            :key="month.label"
            class="flex flex-col items-center gap-2 flex-1"
          >
            <div class="text-[11px] font-semibold text-slate-500 tabular-nums">
              {{ month.count }}
            </div>
            <div
              class="w-full max-w-[34px] rounded-t bg-primary-lightest"
              :style="{ height: `${(month.count / monthlyMax) * 100}px` }"
            />
            <div class="text-[10px] text-slate-500">
              {{ month.label }}
            </div>
          </div>
        </div>
      </LoyaltyCardChartCard>
    </div>

    <!-- Complementary metrics available today -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <LoyaltyCardKpiCard
        label="Moyenne d'âge"
        icon="mdi-account-outline"
        :value="averageAge !== null ? `${averageAge} ans` : null"
      />
      <LoyaltyCardKpiCard
        label="Achats par patient"
        icon="mdi-cart-outline"
        :value="formatDecimal(averageCart)"
        :subtitle="healthcenterCart !== null ? `moy. réseau ${formatDecimal(healthcenterCart)}` : null"
      />
      <LoyaltyCardKpiCard
        v-if="isPoint"
        label="Solde total de points"
        icon="mdi-star-four-points-outline"
        :value="`${formatInt(pointBalance)} pts`"
        :subtitle="pointBalanceEuro !== null ? formatEuro(pointBalanceEuro) : null"
      />
      <LoyaltyCardKpiCard
        v-if="isProduct"
        label="Produits à offrir"
        icon="mdi-package-variant-closed"
        :value="formatInt(unitBalance)"
        subtitle="récompenses en attente au comptoir"
      />
    </div>
  </div>

  <div v-else class="flex justify-center my-10">
    <p class="font-bold">
      Une erreur s'est produite, réessayez plus tard
    </p>
  </div>
</template>

<script lang="ts" setup>
import {computed, ref} from "vue";
import {useRoute, useRouter} from "vue-router";
import useLoyaltyCardAnalysis from "@/loyalty-card/composables/useLoyaltyCardAnalysis";
import useSwr from "@/core/composables/swr/useSwr";
import LoyaltyCardService from "@/loyalty-card/services/LoyaltyCardService";
import {OFFER_DISTANCE} from "@/loyalty-card-membership/services/membershipProgress";
import {LOYALTY_CARD_FILL_COLORS, LOYALTY_CARD_GENDER_COLORS} from "@/loyalty-card/constants";
import {currencyze} from "@/core/filters/Currencyze";
import {numberFormat} from "@/core/filters/NumberFormat";
import {loyaltyCardApi} from "@/container";
import fileHelper from "@/core/helpers/fileHelper";
import {useToastStore} from "@/stores/toast.store";
import BaseFlexSpinner from "@/core/components/base/spinner/BaseFlexSpinner.vue";
import Icon from "@/core/components/icon/Icon.vue";
import LoyaltyCardKpiCard from "@/program/components/loyalty-card-statistics/LoyaltyCardKpiCard.vue";
import LoyaltyCardProgramBand from "@/program/components/loyalty-card-statistics/LoyaltyCardProgramBand.vue";
import LoyaltyCardGenderChart from "@/program/components/loyalty-card-statistics/LoyaltyCardGenderChart.vue";
import LoyaltyCardChartCard from "@/program/components/loyalty-card-statistics/LoyaltyCardChartCard.vue";

const props = defineProps<{
    programmableId: number;
}>();

const {data: loyaltyCard, isLoading} = useLoyaltyCardAnalysis(props.programmableId);
const {hasPointRewards, hasProductRewards, resolveRewardKind, objectiveUnitLabel} = LoyaltyCardService();
const {showError} = useToastStore();
const router = useRouter();
const route = useRoute();

/** The analysis block, or undefined when absent (error / empty response). */
const analysis = computed(() => loyaltyCard.value?.analysis);

const programName = computed(() => loyaltyCard.value?.name ?? "Programme de fidélité");
const organizationName = computed(() => loyaltyCard.value?.organization?.name ?? null);

/** Reward-rule label, from the first objective teaser, falling back to the point teaser. */
const ruleLabel = computed(() => {
    const card = loyaltyCard.value;

    if (!card) {
        return null;
    }

    const objectiveTeaser = card.objectives?.find(objective => objective.reward?.teaser)?.reward?.teaser;

    return objectiveTeaser ?? card.point?.teaser ?? null;
});

const isPoint = computed(() => (loyaltyCard.value ? hasPointRewards(loyaltyCard.value) : false));
const isProduct = computed(() => (loyaltyCard.value ? hasProductRewards(loyaltyCard.value) : false));

/** Unit of the objectives progression: "produit" (quantity) or "€" (amount). */
const objectiveUnit = computed(() => (loyaltyCard.value ? objectiveUnitLabel(loyaltyCard.value) : "produit"));

/**
 * "Proches du seuil" subtitle, adapted to the card reward kind and objective
 * unit. A stamp card counts as near from the moment its product can be
 * offered; the other cards stay one unit away.
 */
const nearThresholdSubtitle = computed(() => {
    const kind = loyaltyCard.value ? resolveRewardKind(loyaltyCard.value) : "product";
    const complement = {
        product: "de la récompense",
        point: "du gain de points",
        discount: "de la remise"
    }[kind];

    if (kind === "product" && objectiveUnit.value === "produit") {
        return `cartes à ${OFFER_DISTANCE} produits ${complement}`;
    }

    return `cartes à 1 ${objectiveUnit.value} ${complement}`;
});

// --- KPI values available from the current analysis endpoint ---
const membershipCount = computed(() => analysis.value?.count?.membership ?? null);
const genderCount = computed(() => ({
    male: analysis.value?.count?.male ?? 0,
    female: analysis.value?.count?.female ?? 0
}));
const averageCartPrice = computed(() => analysis.value?.average?.cart_price?.current ?? null);
const averageCart = computed(() => analysis.value?.average?.cart ?? null);
const healthcenterCart = computed(() => analysis.value?.healthcenter_average?.cart ?? null);
const averageAge = computed(() => {
    const age = analysis.value?.average?.age;

    return age === null || age === undefined ? null : Math.round(age);
});
const pointBalance = computed(() => analysis.value?.total?.point_balance ?? null);
const unitBalance = computed(() => analysis.value?.total?.unit_balance ?? null);
const pointBalanceEuro = computed(() => {
    const balance = analysis.value?.total?.point_balance;

    if (balance === null || balance === undefined) {
        return null;
    }

    return balance * (loyaltyCard.value?.point?.rate || 1);
});

/** Average cart-price evolution vs. the previous period. */
const cartTrend = computed(() => {
    const current = analysis.value?.average?.cart_price?.current;
    const previous = analysis.value?.average?.cart_price?.previous;

    if (current === null || current === undefined || !previous) {
        return null;
    }

    const delta = current - previous;

    if (delta === 0) {
        return null;
    }

    return {
        direction: delta > 0 ? "up" as const : "down" as const,
        label: `${delta > 0 ? "+" : ""}${formatEuro(delta)} vs période préc.`
    };
});

// --- Degradable KPI values ---
// `undefined` = field not served (older backend) → "Bientôt disponible";
// `null` = served but not computable from the data → "—" + explanatory subtitle.
const revenue = computed(() => analysis.value?.total?.revenue);
const rewardsSummary = computed(() => analysis.value?.rewards);
const nearThreshold = computed(() => analysis.value?.count?.near_threshold);
const frequency = computed(() => analysis.value?.average?.purchase_frequency_days);

// --- Charts data (rendered when served, pending otherwise) ---
const progressionColors = LOYALTY_CARD_FILL_COLORS;
const hasProgression = computed(() => (analysis.value?.progression?.length ?? 0) > 0);
const progressionMax = computed(() => Math.max(1, ...(analysis.value?.progression?.map(bucket => bucket.count) ?? [0])));
const progressionSubtitle = computed(() => {
    if (!hasProgression.value || membershipCount.value === null) {
        return null;
    }

    const unitLabel = objectiveUnit.value === "€" ? "montant d'achats (€)" : "nombre de produits";

    return `${formatInt(membershipCount.value)} cartes · ${unitLabel}`;
});

const hasAgePyramid = computed(() => (analysis.value?.age_pyramid?.length ?? 0) > 0);
const pyramidMax = computed(() => Math.max(
    1,
    ...(analysis.value?.age_pyramid?.flatMap(bucket => [bucket.male, bucket.female]) ?? [0])
));

const hasMonthlyOpenings = computed(() => (analysis.value?.monthly_openings?.length ?? 0) > 0);
const monthlyMax = computed(() => Math.max(1, ...(analysis.value?.monthly_openings?.map(month => month.count) ?? [0])));

// --- Navigation ---
const canOpenConsumers = computed(() => !!route.params.programId);
const goToConsumers = () => {
    if (!route.params.programId) {
        return;
    }

    router.push({name: "program.detail.consumers", params: {programId: route.params.programId}});
};

const goToRewards = () => {
    if (!route.params.programId) {
        return;
    }

    router.push({
        name: "program.loyalty-card.rewards",
        params: {programId: route.params.programId, loyaltyCardId: props.programmableId}
    });
};

const goToNearThreshold = () => {
    if (!route.params.programId) {
        return;
    }

    router.push({
        name: "program.detail.consumers",
        params: {programId: route.params.programId},
        query: {fill: "near_threshold"}
    });
};

// --- Convertible big buyers (paper → digital migration) ---

// SWR-cached aside from the analysis: coming back to the dashboard shows the
// cached count instantly (revalidated in background) and the ES aggregation
// never delays the KPI grid.
const {data: bigBuyers} = useSwr(
    `/loyalty-cards/${props.programmableId}/big-buyers`,
    () => loyaltyCardApi().getBigBuyers(props.programmableId)
);

/** Big buyers of the card's range without a digital card yet; null while loading. */
const convertibleCount = computed(() => (bigBuyers.value ? bigBuyers.value.data.length : null));

const goToMigration = () => {
    if (!route.params.programId) {
        return;
    }

    router.push({
        name: "program.loyalty-card.migration",
        params: {programId: route.params.programId, loyaltyCardId: props.programmableId}
    });
};

// --- Unified CSV export (cards + delivered rewards) ---
const exporting = ref(false);
const canExport = computed(() => !!analysis.value && !exporting.value);

/**
 * Export the program data as a single CSV built server-side (cards, then
 * delivered rewards).
 */
const exportAll = async () => {
    if (!canExport.value) {
        return;
    }

    exporting.value = true;

    try {
        const blob = await loyaltyCardApi().exportAnalysis(props.programmableId);

        const slug = programName.value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
        const date = new Date().toISOString().slice(0, 10);

        fileHelper.downloadBlob(`carte-fidelite-${slug || "programme"}-${date}.csv`, blob);
    } catch {
        showError({content: "L'export a échoué, aucune donnée n'a été téléchargée. Réessayez plus tard."});
    } finally {
        exporting.value = false;
    }
};

// --- Formatting helpers (shared app formatters, "—" when the value is absent) ---
function formatEuro (value?: number | null): string {
    return value === null || value === undefined ? "—" : currencyze(value);
}

function formatInt (value?: number | null): string {
    return value === null || value === undefined ? "—" : numberFormat(value, 0).toString();
}

function formatDecimal (value?: number | null): string {
    return value === null || value === undefined ? "—" : numberFormat(value, 2).toString();
}
</script>
